import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { HeroCarousel } from "@/components/home/HeroCarousel";
import { CategoriesRow } from "@/components/home/CategoriesRow";
import { ProductRail } from "@/components/home/ProductRail";
import { TwoGrades } from "@/components/home/TwoGrades";
import { HowWeCheck } from "@/components/home/HowWeCheck";
import { ReviewsFaq, ReviewsMarquee, FaqSection } from "@/components/home/ReviewsFaq";
import { ProductCard, ProductCardSkeleton } from "@/components/ProductCard";
import { getProducts, getHomepage, getHomepagePreview, getMeta, getFeaturedReviews } from "@/lib/api";
import { Star } from "lucide-react";

// Homepage attuale, preservata così com'è (sezione "legacy_default").
function LegacyHome({ products }) {
  const offers = products === null ? null : products.filter((p) => p.on_sale).slice(0, 8);
  const recommended = products === null ? null : products.filter((p) => p.featured).concat(products).slice(0, 8);
  const skeletons = Array.from({ length: 4 }).map((_, i) => <ProductCardSkeleton key={i} />);
  return (
    <>
      <HeroCarousel />
      <CategoriesRow />
      {offers && offers.length > 0 && (
        <ProductRail title="Le migliori offerte" link={{ to: "/offerte", label: "Vedi tutte", testId: "home-offers-link" }} testid="home-offers">
          {offers.map((p) => <ProductCard key={p.id} product={p} />)}
        </ProductRail>
      )}
      <ProductRail title="Consigliati per te" link={{ to: "/catalogo", label: "Vedi tutti", testId: "home-recommended-link" }} testid="home-recommended">
        {recommended === null ? skeletons : recommended.map((p) => <ProductCard key={p.id} product={p} />)}
      </ProductRail>
      <TwoGrades />
      <HowWeCheck />
      <ReviewsFaq />
    </>
  );
}

function Wrap({ children }) {
  return <section className="mx-auto w-full max-w-page px-4 py-8 sm:px-6 lg:px-8">{children}</section>;
}

function autoProducts(products, s) {
  if (!products) return null;
  let list = [...products];
  if (s.rule === "featured") list = list.filter((p) => p.featured);
  else if (s.rule === "on_sale") list = list.filter((p) => p.on_sale);
  else if (s.rule === "new") list = list.filter((p) => p.is_new);
  else if (s.rule === "cheapest") list.sort((a, b) => (a.min_price || 0) - (b.min_price || 0));
  else if (s.rule === "category") list = list.filter((p) => p.category === s.rule_value);
  else if (s.rule === "brand") list = list.filter((p) => p.brand === s.rule_value);
  if (s.sort === "prezzo_asc") list.sort((a, b) => (a.min_price || 0) - (b.min_price || 0));
  else if (s.sort === "prezzo_desc") list.sort((a, b) => (b.min_price || 0) - (a.min_price || 0));
  else if (s.sort === "novita") list.sort((a, b) => (b.sort_order || 0) - (a.sort_order || 0));
  return list.slice(0, s.count || 8);
}

function Section({ s, products, meta, reviews }) {
  if (s.type === "legacy_default") return <LegacyHome products={products} />;

  // --- Blocchi nativi: rendono i componenti originali della homepage, identici,
  //     ma singolarmente attivabili/riordinabili e con titoli modificabili. ---
  if (s.type === "block_hero") return <HeroCarousel slides={s.slides} />;
  if (s.type === "block_categories") return <CategoriesRow title={s.title || undefined} />;
  if (s.type === "block_offers") {
    const offers = products === null ? null : products.filter((p) => p.on_sale).slice(0, 8);
    if (offers && offers.length === 0) return null;
    return (
      <ProductRail
        title={s.title || "Le migliori offerte"}
        link={{ to: s.see_all_link || "/offerte", label: "Vedi tutte", testId: "home-offers-link" }}
        testid="home-offers"
      >
        {offers === null
          ? Array.from({ length: 4 }).map((_, i) => <ProductCardSkeleton key={i} />)
          : offers.map((p) => <ProductCard key={p.id} product={p} />)}
      </ProductRail>
    );
  }
  if (s.type === "block_recommended") {
    const recommended = products === null ? null : products.filter((p) => p.featured).concat(products).slice(0, 8);
    return (
      <ProductRail
        title={s.title || "Consigliati per te"}
        link={{ to: s.see_all_link || "/catalogo", label: "Vedi tutti", testId: "home-recommended-link" }}
        testid="home-recommended"
      >
        {recommended === null
          ? Array.from({ length: 4 }).map((_, i) => <ProductCardSkeleton key={i} />)
          : recommended.map((p) => <ProductCard key={p.id} product={p} />)}
      </ProductRail>
    );
  }
  if (s.type === "block_two_grades") return <TwoGrades />;
  if (s.type === "block_how_we_check") return <HowWeCheck title={s.title || undefined} subtitle={s.subtitle || undefined} />;
  if (s.type === "block_reviews") return <ReviewsMarquee title={s.title || undefined} subtitle={s.subtitle || undefined} />;
  if (s.type === "block_faq") return <FaqSection title={s.title || undefined} />;

  if (s.type === "reviews_featured") {
    let list = reviews || [];
    if (s.review_ids && s.review_ids.length) {
      const pos = new Map(s.review_ids.map((id, i) => [id, i]));
      list = [...list].filter((r) => pos.has(r.id)).sort((a, b) => pos.get(a.id) - pos.get(b.id));
    }
    if (!list.length) return null;
    return (
      <Wrap>
        <h2 className="font-display text-2xl font-extrabold tracking-tight text-foreground sm:text-3xl">{s.title || "Cosa dicono i clienti"}</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {list.slice(0, 6).map((r) => (
            <div key={r.id} className="rounded-2xl bg-card p-5">
              <span className="inline-flex">{[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`h-4 w-4 ${i <= r.stars ? "fill-lime text-lime" : "text-muted-foreground/40"}`} />)}</span>
              {r.title && <p className="mt-2 font-display text-base font-bold text-foreground">{r.title}</p>}
              <p className="mt-1 line-clamp-4 text-sm text-muted-foreground">{r.text}</p>
              <p className="mt-3 text-xs text-muted-foreground">{r.display_name}{r.city ? ` · ${r.city}` : ""} · {r.product_name}</p>
            </div>
          ))}
        </div>
      </Wrap>
    );
  }

  if (s.type === "hero") {
    const slide = (s.slides || [])[0];
    if (!slide) return null;
    return (
      <div className="relative overflow-hidden">
        {slide.image_desktop && <img src={slide.image_desktop} alt={slide.title || ""} className="hidden h-[46vh] w-full object-cover sm:block" />}
        {slide.image_mobile && <img src={slide.image_mobile} alt={slide.title || ""} className="block aspect-[4/5] w-full object-cover sm:hidden" />}
        <div className="mx-auto w-full max-w-page px-4 py-10 sm:px-6 lg:px-8">
          {slide.title && <h2 className="font-display text-3xl font-extrabold tracking-tight text-foreground sm:text-5xl">{slide.title}</h2>}
          {slide.subtitle && <p className="mt-2 max-w-xl text-muted-foreground">{slide.subtitle}</p>}
          {slide.cta_label && slide.link && (
            <Link to={slide.link} className="mt-5 inline-flex h-11 items-center rounded-full bg-lime px-6 text-sm font-bold text-ink">{slide.cta_label}</Link>
          )}
        </div>
      </div>
    );
  }

  if (s.type === "showcase_manual") {
    const ids = s.product_ids || [];
    const map = new Map((products || []).map((p) => [p.id, p]));
    const list = products === null ? null : ids.map((id) => map.get(id)).filter(Boolean);
    return (
      <ProductRail title={s.title || "Vetrina"} subtitle={s.subtitle} link={s.see_all_link ? { to: s.see_all_link, label: "Vedi tutti" } : undefined}>
        {list === null ? Array.from({ length: 4 }).map((_, i) => <ProductCardSkeleton key={i} />) : list.map((p) => <ProductCard key={p.id} product={p} />)}
      </ProductRail>
    );
  }

  if (s.type === "showcase_auto") {
    const list = autoProducts(products, s);
    return (
      <ProductRail title={s.title || "Vetrina"}>
        {list === null ? Array.from({ length: 4 }).map((_, i) => <ProductCardSkeleton key={i} />) : list.map((p) => <ProductCard key={p.id} product={p} />)}
      </ProductRail>
    );
  }

  if (s.type === "featured_categories") {
    const labels = {}; (meta?.categories || []).forEach((c) => (labels[c.key] = c.label));
    return (
      <Wrap>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {(s.category_keys || []).map((k) => (
            <Link key={k} to={`/catalogo?category=${k}`} className="rounded-2xl bg-card p-6 text-center font-display text-lg font-bold text-foreground transition-colors hover:bg-surface3">
              {labels[k] || k}
            </Link>
          ))}
        </div>
      </Wrap>
    );
  }

  if (s.type === "brands") {
    const labels = {}; (meta?.brands || []).forEach((b) => (labels[b.key] = b.name));
    return (
      <Wrap>
        <div className="flex flex-wrap gap-3">
          {(s.brand_keys || []).map((k) => (
            <Link key={k} to={`/catalogo?brand=${k}`} className="rounded-full bg-card px-5 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-surface3">
              {labels[k] || k}
            </Link>
          ))}
        </div>
      </Wrap>
    );
  }

  if (s.type === "banner") {
    return (
      <Wrap>
        <div className="relative overflow-hidden rounded-3xl bg-card">
          {s.image_desktop && <img src={s.image_desktop} alt={s.title || ""} className="hidden max-h-[320px] w-full object-cover sm:block" />}
          {s.image_mobile && <img src={s.image_mobile} alt={s.title || ""} className="block w-full object-cover sm:hidden" />}
          <div className="p-8">
            {s.title && <h3 className="font-display text-2xl font-extrabold text-foreground">{s.title}</h3>}
            {s.text && <p className="mt-2 max-w-lg text-muted-foreground">{s.text}</p>}
            {s.cta_label && s.link && <Link to={s.link} className="mt-4 inline-flex h-11 items-center rounded-full bg-lime px-6 text-sm font-bold text-ink">{s.cta_label}</Link>}
          </div>
        </div>
      </Wrap>
    );
  }

  if (s.type === "benefits") {
    const items = (s.items || []).filter((i) => i.title || i.text);
    return (
      <Wrap>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((it, i) => (
            <div key={i} className="rounded-2xl bg-card p-5">
              <p className="font-display text-lg font-bold text-foreground">{it.title}</p>
              {it.text && <p className="mt-1 text-sm text-muted-foreground">{it.text}</p>}
            </div>
          ))}
        </div>
      </Wrap>
    );
  }

  return null;
}

export default function Home() {
  const location = useLocation();
  const preview = new URLSearchParams(location.search).get("preview") === "1";
  const [config, setConfig] = useState(null);
  const [products, setProducts] = useState(null);
  const [meta, setMeta] = useState(null);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    const loader = preview ? getHomepagePreview().then((d) => d.draft) : getHomepage();
    loader.then(setConfig).catch(() => setConfig({ sections: [{ type: "legacy_default", enabled: true }] }));
    getProducts({ limit: 200 }).then(setProducts).catch(() => setProducts([]));
    getMeta().then(setMeta).catch(() => setMeta({ categories: [], brands: [] }));
    getFeaturedReviews().then(setReviews).catch(() => setReviews([]));
  }, [preview]);

  const sections = (config?.sections || [{ type: "legacy_default", enabled: true }]).filter((s) => s.enabled !== false);

  return (
    <div data-testid="home-page">
      {preview && (
        <div className="bg-lime px-4 py-2 text-center text-sm font-semibold text-ink" data-testid="home-preview-banner">
          Anteprima bozza — visibile solo a te
        </div>
      )}
      {sections.map((s) => <Section key={s.id || s.type} s={s} products={products} meta={meta} reviews={reviews} />)}
    </div>
  );
}
